import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import api from '../lib/axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { 
  Plus, Edit, Trash2, Search, FileText, Send, MessageCircle, 
  CheckCircle, Clock, XCircle, Eye, ShoppingCart, 
  Building2, Package, ChevronRight, ChevronLeft, Filter,
  DollarSign, Calendar, TrendingUp, Check, Truck, Users,
  Download, X, Paperclip, Upload, CreditCard, RefreshCw, ChevronDown, ChevronUp, Trophy, Bot
} from 'lucide-react';
import { toast } from 'sonner';
import { TitleWithHelp, HelpTooltip } from '../components/ui/help-tooltip';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import AnexosManager from '../components/AnexosManager';
import AnexosErrorBoundary from '../components/AnexosErrorBoundary';
import CadastroRapidoProdutoDialog from '../components/CadastroRapidoProdutoDialog';

// Importar componentes refatorados
import { 
  StatusBadge, 
  API, 
  getAuthHeader, 
  formatCurrency,
  getFornecedorNome 
} from '../components/compras/utils';
import ParcelasForm from '../components/compras/ParcelasForm';
import ComprasFilters, { computeDateRange } from '../components/compras/ComprasFilters';
import EnviarPedidoDialog from '../components/compras/EnviarPedidoDialog';
import PedidoDetalhesDialog from '../components/compras/PedidoDetalhesDialog';
import CotacaoList from '../components/compras/CotacaoList';
import PedidoList from '../components/compras/PedidoList';
import RespostaFornecedorForm from '../components/compras/RespostaFornecedorForm';
import MesaDecisao from '../components/compras/MesaDecisao';
import CotacaoFormSteps from '../components/compras/CotacaoFormSteps';
import { CurrencyInput } from '../components/ui/currency-input';
import { getCurrencyMeta } from '../lib/currencies';
import DialogReceberPedido from '../components/compras/DialogReceberPedido';
import DashboardCompras from '../components/compras/DashboardCompras';
import ConferenciaCustosDialog from '../components/compras/ConferenciaCustosDialog';
import AssistenteCompras from '../components/compras/AssistenteCompras';
import useComprasData from '../components/compras/useComprasData';
import ComprasConfirmDialogs from '../components/compras/ComprasConfirmDialogs';
import CotacaoViewDialog from '../components/compras/CotacaoViewDialog';
import CotacaoEditDialog from '../components/compras/CotacaoEditDialog';
import EnviarCotacaoDialog from '../components/compras/EnviarCotacaoDialog';
import WhatsAppEnvioDialog from '../components/compras/WhatsAppEnvioDialog';
import CadastroRapidoFornecedorDialog from '../components/compras/CadastroRapidoFornecedorDialog';
import PdfPreviewInlineDialog from '../components/compras/PdfPreviewInlineDialog';

// Configuração de ajuda do módulo
const HELP_CONFIG = {
  title: "Módulo de Compras",
  description: "Gerencie todo o processo de compras: desde a criação de cotações, envio para fornecedores, análise comparativa de respostas, até a geração de pedidos de compra com controle de parcelas e recebimento.",
  connections: [
    { module: "Empresas", description: "A empresa selecionada define o cabeçalho e dados fiscais nas cotações e pedidos." },
    { module: "Fornecedores", description: "Cotações são enviadas para fornecedores cadastrados. Respostas são registradas por fornecedor." },
    { module: "Produtos", description: "Itens das cotações podem vir do cadastro de produtos ou serem digitados manualmente." },
    { module: "Projetos", description: "Cotações e pedidos podem ser vinculados a projetos para controle de custos." },
    { module: "Conta Azul", description: "Pedidos aprovados podem criar automaticamente contas a pagar no Conta Azul." },
    { module: "Estoque", description: "Ao receber um pedido, os itens são adicionados ao estoque automaticamente." }
  ],
  tips: [
    "Use o fluxo: Cotação → Enviar para fornecedores → Registrar respostas → Comparar → Gerar Pedido.",
    "O comparativo de respostas ajuda a escolher o melhor fornecedor para cada item.",
    "Configure parcelas no pedido para controle financeiro detalhado.",
    "Ao receber mercadorias, selecione o local de armazenamento para controle de estoque."
  ]
};

const Compras = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  // Camada de dados centralizada no hook useComprasData (states de referência + fetches)
  const {
    cotacoes, setCotacoes,
    pedidos, setPedidos,
    empresas, setEmpresas,
    fornecedores, setFornecedores,
    tiposServico, setTiposServico,
    tiposProduto, setTiposProduto,
    projetos, setProjetos,
    produtos, setProdutos,
    categorias, marcas,
    aprovadores, setAprovadores,
    loading, setLoading,
    statsVersion, setStatsVersion,
    fetchCotacoes,
    fetchPedidos,
    fetchEmpresas,
    fetchFornecedores,
    fetchTiposServico,
    fetchProjetos,
    fetchProdutos,
    fetchTiposProduto,
    fetchDashboardStats,
    fetchEvolucaoMensal,
    loadInitialData,
  } = useComprasData();
  const [searchTerm, setSearchTerm] = useState('');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');

  // ===== FILTROS AVANÇADOS (multi-seleção) =====
  // Cotações
  const [cotStatusSel, setCotStatusSel] = useState([]);
  // Modo de visualização das cotações (Issue: cotações aprovadas poluindo a lista)
  // 'em_andamento' (padrão) | 'concluidas' | 'todas'
  const [cotViewMode, setCotViewMode] = useState('em_andamento');
  const [cotFornecedorSel, setCotFornecedorSel] = useState([]);
  const [cotProjetoSel, setCotProjetoSel] = useState([]);
  const [cotItemSel, setCotItemSel] = useState([]);
  const [cotMoedaSel, setCotMoedaSel] = useState([]);
  const [cotUrgenteSel, setCotUrgenteSel] = useState(false);
  const [cotPeriodo, setCotPeriodo] = useState('todos');
  // Pedidos
  const [pedStatusSel, setPedStatusSel] = useState([]);
  const [pedFornecedorSel, setPedFornecedorSel] = useState([]);
  const [pedProjetoSel, setPedProjetoSel] = useState([]);
  const [pedItemSel, setPedItemSel] = useState([]);
  const [pedMoedaSel, setPedMoedaSel] = useState([]);
  const [pedUrgenteSel, setPedUrgenteSel] = useState(false);
  const [pedPeriodo, setPedPeriodo] = useState('todos');
  // Dashboard (só datas)
  const [dashPeriodo, setDashPeriodo] = useState('todos');
  
  // Dialogs
  const [showCotacaoDialog, setShowCotacaoDialog] = useState(false);
  const [showCotacaoView, setShowCotacaoView] = useState(null);
  const [expandedFornecedor, setExpandedFornecedor] = useState(null);
  const [showRespostaDialog, setShowRespostaDialog] = useState(null);
  // Contador de alterações pendentes da resposta — para proteção ao fechar dialog
  const [respostaPendingCount, setRespostaPendingCount] = useState(0);
  const [confirmCloseResposta, setConfirmCloseResposta] = useState(false);
  const [showMesaDecisao, setShowMesaDecisao] = useState(null);
  const [showWhatsAppDialog, setShowWhatsAppDialog] = useState(null);
  const [showEnviarDialog, setShowEnviarDialog] = useState(null);
  const [showEnviarPedidoDialog, setShowEnviarPedidoDialog] = useState(null); // Modal para enviar pedido
  const [showPedidoDialog, setShowPedidoDialog] = useState(null);
  const [showParcelasDialog, setShowParcelasDialog] = useState(null); // Modal para definir parcelas
  const [deleteCotacaoId, setDeleteCotacaoId] = useState(null);
  const [deletePedidoId, setDeletePedidoId] = useState(null);
  const [deleteMotivo, setDeleteMotivo] = useState('');
  const currentUser = JSON.parse(localStorage.getItem('nautilus_user') || '{}');
  const isAdmin = currentUser.role === 'admin';
  const [showCadastroRapidoProduto, setShowCadastroRapidoProduto] = useState(false);
  const [showCadastroRapidoFornecedor, setShowCadastroRapidoFornecedor] = useState(false);
  const [cotacaoStep, setCotacaoStep] = useState(1); // 1=Info, 2=Itens, 3=Fornecedores
  const [showPdfPreview, setShowPdfPreview] = useState(null); // {type: 'cotacao'|'pedido', id: string, numero: string}
  const [showReceberDialog, setShowReceberDialog] = useState(null); // Modal para receber pedido com local de armazenamento
  const [showConferenciaDialog, setShowConferenciaDialog] = useState(null); // Modal de conferência de custos do projeto
  const [assistenteTotal, setAssistenteTotal] = useState(0); // Contador de pendências do Assistente (badge na aba)
  
  // Form
  const [editingCotacao, setEditingCotacao] = useState(null);
  // Confirmação de edição em cotação com respostas registradas
  const [confirmEditCotacao, setConfirmEditCotacao] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);

  const emptyCotacaoForm = {
    empresa_id: '',
    titulo: '',
    descricao: '',
    moeda: 'BRL',
    taxa_cambio: '',
    incoterm: '',
    validade_proposta: '',
    transit_time_dias: '',
    lead_time_dias: '',
    porto_origem: '',
    data_limite_resposta: '',
    validade_dias: 30,
    itens: [],
    fornecedores: [],
    projeto_id: '',
    projeto_nome: '',
    observacao: '',
    urgente: false,
    responsaveis_aprovacao_ids: [],
  };

  const [cotacaoForm, setCotacaoForm] = useState(emptyCotacaoForm);
  const [fornSearchCot, setFornSearchCot] = useState(''); // busca de fornecedores no edit/modal de cotação

  // Aprovadores disponíveis (feature compras_aprovar) — dados no hook useComprasData
  const [aprovadorSearch, setAprovadorSearch] = useState('');
  
  // Anexos
  const [uploadingAnexo, setUploadingAnexo] = useState(false);
  const [anexos, setAnexos] = useState([]);
  const [anexosCotacaoForm, setAnexosCotacaoForm] = useState([]);
  // Buffer local de anexos antes de salvar uma cotação nova (a cotação ainda não tem ID)
  const [anexosPendentes, setAnexosPendentes] = useState([]); // [{ file, nome, item_id, tempId }]
  const [isDragging, setIsDragging] = useState(false);

  // Stats reais vindas do servidor (sem paginação)
  const [dashData, setDashData] = useState(null);
  const [evolucaoData, setEvolucaoData] = useState(null);
  const [dashMoeda, setDashMoeda] = useState('BRL');

  useEffect(() => {
    document.title = "Compras — NautilusOS";
  }, []);

  // Define empresa padrão no formulário quando houver apenas uma cadastrada
  useEffect(() => {
    if (empresas.length === 1) {
      setCotacaoForm(prev => ({ ...prev, empresa_id: empresas[0].id }));
    }
  }, [empresas]);

  // Carregar anexos quando visualizar cotação
  useEffect(() => {
    if (showCotacaoView?.id) {
      fetchAnexos(showCotacaoView.id);
    } else {
      setAnexos([]);
    }
  }, [showCotacaoView?.id]);

  // Carregar anexos quando abrir o form de edição
  useEffect(() => {
    if (showCotacaoDialog && cotacaoForm.id) {
      fetchAnexosForm(cotacaoForm.id);
    } else {
      setAnexosCotacaoForm([]);
    }
  }, [showCotacaoDialog, cotacaoForm.id]);


  // ========== COTAÇÃO CRUD ==========

  const handleNovaCotacao = () => {
    setEditingCotacao(null);
    setCotacaoForm({
      ...emptyCotacaoForm,
      empresa_id: empresas.length === 1 ? empresas[0].id : '',
    });
    setCotacaoStep(1);
    setAnexosPendentes([]); // limpa buffer de anexos pendentes
    setShowCotacaoDialog(true);
  };

  const handleEditCotacao = (cotacao) => {
    // Se está em status 'analisando' (já tem respostas registradas) ou já enviada,
    // exige confirmação explícita antes de abrir o form de edição.
    const hasResponses = (cotacao.fornecedores || []).some(f => f.status === 'respondido' || f.resposta);
    const isInProgress = cotacao.status === 'analisando' || (cotacao.status === 'aguardando_respostas' && hasResponses);
    if (isInProgress) {
      setConfirmEditCotacao(cotacao);
      return;
    }
    abrirFormEdicaoCotacao(cotacao);
  };

  const abrirFormEdicaoCotacao = (cotacao) => {
    setEditingCotacao(cotacao);
    setCotacaoForm({
      id: cotacao.id || '',
      empresa_id: cotacao.empresa_id || '',
      titulo: cotacao.titulo || '',
      descricao: cotacao.descricao || '',
      moeda: cotacao.moeda || 'BRL',
      taxa_cambio: cotacao.taxa_cambio ?? '',
      incoterm: cotacao.incoterm || '',
      validade_proposta: cotacao.validade_proposta ? cotacao.validade_proposta.split('T')[0] : '',
      transit_time_dias: cotacao.transit_time_dias ?? '',
      lead_time_dias: cotacao.lead_time_dias ?? '',
      porto_origem: cotacao.porto_origem || '',
      data_limite_resposta: cotacao.data_limite_resposta ? cotacao.data_limite_resposta.split('T')[0] : '',
      validade_dias: cotacao.validade_dias || 30,
      itens: cotacao.itens || [],
      fornecedores: cotacao.fornecedores || [],
      projeto_id: cotacao.projeto_id || '',
      projeto_nome: cotacao.projeto_nome || '',
      observacao: cotacao.observacao || '',
      urgente: cotacao.urgente || false,
      responsaveis_aprovacao_ids: ((cotacao.aprovacao || {}).responsaveis || []).map(r => r.id),
    });
    setCotacaoStep(1);
    setShowCotacaoDialog(true);
  };

  // Busca cotação atualizada antes de abrir modal de resposta
  const handleOpenRespostaDialog = async (cotacao) => {
    try {
      const response = await api.get(`/cotacoes/${cotacao.id}`);
      setShowRespostaDialog(response.data);
    } catch (error) {
      console.error('Erro ao buscar cotação:', error);
      // Fallback para a cotação local
      setShowRespostaDialog(cotacao);
    }
  };

  // Busca pedido com campos hidratados (frete, garantia, etc) ao abrir detalhes
  const handleOpenPedidoDetalhes = async (pedido) => {
    try {
      const response = await api.get(`/pedidos-compra/${pedido.id}`);
      setShowPedidoDialog(response.data);
    } catch (error) {
      console.error('Erro ao buscar pedido:', error);
      setShowPedidoDialog(pedido);
    }
  };

  // Busca cotação atualizada e abre Mesa de Decisão
  const handleOpenMesaDecisao = async (cotacao) => {
    try {
      const response = await api.get(`/cotacoes/${cotacao.id}`);
      setShowMesaDecisao(response.data);
    } catch (error) {
      console.error('Erro ao buscar cotação:', error);
      toast.error('Erro ao carregar comparativo');
      setShowMesaDecisao(cotacao);
    }
  };

  // Deep-link (ex.: link de WhatsApp do Assistente/Z-API, ou Histórico de Pedidos do
  // projeto): /compras?cotacao_id=xxx abre a Mesa de Decisão; /compras?pedido_id=xxx
  // abre o pedido já selecionado no modal de Detalhes.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cotacaoId = params.get('cotacao_id');
    const pedidoId = params.get('pedido_id');
    if (!cotacaoId && !pedidoId) return;

    const limparParam = (chave) => {
      params.delete(chave);
      const rest = params.toString();
      window.history.replaceState({}, '', rest ? `${window.location.pathname}?${rest}` : window.location.pathname);
    };

    if (cotacaoId) {
      api.get(`/cotacoes/${cotacaoId}`)
        .then((r) => setShowMesaDecisao(r.data))
        .catch(() => toast.error('Cotação do link não foi encontrada'))
        .finally(() => limparParam('cotacao_id'));
    }
    if (pedidoId) {
      api.get(`/pedidos-compra/${pedidoId}`)
        .then((r) => setShowPedidoDialog(r.data))
        .catch(() => toast.error('Pedido do link não foi encontrado'))
        .finally(() => limparParam('pedido_id'));
    }
  }, []);

  const validarStep1 = () => {
    if (!cotacaoForm.empresa_id) {
      toast.error('Selecione uma empresa');
      return false;
    }
    if (!cotacaoForm.titulo.trim()) {
      toast.error('Informe o título da cotação');
      return false;
    }
    return true;
  };

  const validarStep2 = () => {
    if (cotacaoForm.itens.length === 0) {
      toast.error('Adicione pelo menos um item');
      return false;
    }
    return true;
  };

  const handleAvancar = () => {
    if (cotacaoStep === 1 && validarStep1()) {
      setCotacaoStep(2);
    } else if (cotacaoStep === 2 && validarStep2()) {
      setCotacaoStep(3);
    }
  };

  const handleVoltar = () => {
    if (cotacaoStep > 1) {
      setCotacaoStep(cotacaoStep - 1);
    }
  };

  // Toggle aprovador (responsável pela aprovação da cotação)
  const toggleAprovadorCotacao = (aprov) => {
    setCotacaoForm(prev => {
      const list = prev.responsaveis_aprovacao_ids || [];
      const isSel = list.includes(aprov.id);
      return {
        ...prev,
        responsaveis_aprovacao_ids: isSel
          ? list.filter(id => id !== aprov.id)
          : [...list, aprov.id]
      };
    });
  };

  const handleSaveCotacao = async () => {
    // Validações já feitas nos steps anteriores
    if (!validarStep1() || !validarStep2()) return;

    setSubmitLoading(true);
    try {
      const payload = {
        ...cotacaoForm,
        data_limite_resposta: cotacaoForm.data_limite_resposta || null,
      };

      let cotacaoIdResultante = null;
      if (editingCotacao) {
        await api.put(`/cotacoes/${editingCotacao.id}`, payload);
        cotacaoIdResultante = editingCotacao.id;
        toast.success('Cotação atualizada!');
      } else {
        const r = await api.post('/cotacoes', payload);
        cotacaoIdResultante = r.data?.id || r.data?.cotacao?.id;
        toast.success('Cotação criada!');
      }

      // Upload de anexos pendentes (que foram adicionados ANTES da cotação existir)
      if (cotacaoIdResultante && anexosPendentes.length > 0) {
        const totalPendentes = anexosPendentes.length;
        toast.info(`Enviando ${totalPendentes} anexo(s)...`);
        let ok = 0, fail = 0;
        for (const pend of anexosPendentes) {
          try {
            await handleUploadAnexo(cotacaoIdResultante, pend.file, pend.item_id || null);
            ok++;
          } catch (err) {
            console.error('Erro ao enviar anexo pendente', pend.nome, err);
            fail++;
          }
        }
        if (fail === 0) toast.success(`${ok} anexo(s) enviado(s).`);
        else toast.warning(`${ok} enviado(s), ${fail} falhou(aram).`);
        setAnexosPendentes([]);
      }

      setShowCotacaoDialog(false);
      setCotacaoStep(1);
      fetchCotacoes();
    } catch (error) {
      console.error('Erro ao salvar cotação:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar cotação');
    } finally {
      setSubmitLoading(false);
    }
  };

  // ========== CADASTRO RÁPIDO PRODUTO ==========

  // ========== CADASTRO RÁPIDO FORNECEDOR ==========
  const [novoFornecedorForm, setNovoFornecedorForm] = useState({
    nome: '',
    cnpj: '',
    telefone: '',
    email: '',
    contato_nome: '',
  });

  const handleSaveFornecedorRapido = async () => {
    if (!novoFornecedorForm.nome.trim()) {
      toast.error('Informe o nome do fornecedor');
      return;
    }

    try {
      const response = await api.post('/fornecedores', novoFornecedorForm);
      toast.success('Fornecedor cadastrado!');
      setShowCadastroRapidoFornecedor(false);
      setNovoFornecedorForm({ nome: '', cnpj: '', telefone: '', email: '', contato_nome: '' });
      fetchFornecedores();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cadastrar fornecedor');
    }
  };

  // ========== MEMOIZAÇÕES ==========
  const tiposServicoFiltrados = useMemo(() => 
    tiposServico.filter(ts => {
      const nome = ts.nome.toLowerCase();
      return !nome.startsWith('frete') && !nome.includes('reembolso') && !nome.includes('entrega');
    }),
    [tiposServico]
  );

  // ========== PDF DOWNLOAD ==========
  const handleDownloadPdfCotacao = async (cotacaoId, numero) => {
    try {
      toast.info('Gerando PDF...');
      const response = await api.get(`/cotacoes/${cotacaoId}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Cotacao_${numero.replace('/', '-')}.pdf`);
      link.style.display = 'none';
      link.click();
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
      toast.success('PDF baixado!');
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      toast.error('Erro ao gerar PDF');
    }
  };

  const handleDownloadPdfPedido = async (pedidoId, numero) => {
    try {
      toast.info('Gerando PDF...');
      const response = await api.get(`/pedidos-compra/${pedidoId}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Pedido_${numero.replace('/', '-')}.pdf`);
      link.style.display = 'none';
      link.click();
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
      toast.success('PDF baixado!');
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      toast.error('Erro ao gerar PDF');
    }
  };

  const handlePreviewPdf = (type, id, numero) => {
    setShowPdfPreview({ type, id, numero });
  };

  const getPdfPreviewUrl = () => {
    if (!showPdfPreview) return '';
    const token = localStorage.getItem('nautilus_token');
    const endpoint = showPdfPreview.type === 'cotacao' 
      ? `/api/cotacoes/${showPdfPreview.id}/pdf/preview`
      : `/api/pedidos-compra/${showPdfPreview.id}/pdf/preview`;
    return `${endpoint}?token=${token}`;
  };

  const handleDeleteCotacao = async () => {
    if (!deleteCotacaoId) return;

    try {
      await api.delete(`/cotacoes/${deleteCotacaoId}${deleteMotivo ? `?motivo=${encodeURIComponent(deleteMotivo)}` : ''}`);
      toast.success('Cotação excluída com sucesso!');
      setDeleteCotacaoId(null);
      setDeleteMotivo('');
      fetchCotacoes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir cotação');
    }
  };

  const handleDeletePedido = async () => {
    if (!deletePedidoId) return;

    try {
      const res = await api.delete(`/pedidos-compra/${deletePedidoId}${deleteMotivo ? `?motivo=${encodeURIComponent(deleteMotivo)}` : ''}`);
      const data = res.data;
      let msg = 'Pedido excluído com sucesso!';
      if (data.estoque_revertido?.length > 0) {
        msg += ` Estoque revertido para ${data.estoque_revertido.length} produto(s).`;
      }
      toast.success(msg);
      setDeletePedidoId(null);
      setDeleteMotivo('');
      fetchPedidos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir pedido');
    }
  };

  // ========== ANEXOS ==========
  const fetchAnexos = async (cotacaoId) => {
    try {
      const response = await api.get(`/cotacoes/${cotacaoId}/anexos`);
      setAnexos(response.data || []);
    } catch (error) {
      console.error('Erro ao buscar anexos:', error);
      setAnexos([]);
    }
  };

  const fetchAnexosForm = async (cotacaoId) => {
    try {
      const response = await api.get(`/cotacoes/${cotacaoId}/anexos`);
      setAnexosCotacaoForm(response.data || []);
    } catch (error) {
      console.error('Erro ao buscar anexos do form:', error);
      setAnexosCotacaoForm([]);
    }
  };

  const handleUploadAnexo = async (cotacaoId, file, itemId = null) => {
    if (!file) return;
    
    setUploadingAnexo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (itemId) formData.append('item_id', itemId);
      
      // IMPORTANTE: não setar Content-Type manualmente para FormData
      // (o browser define com o boundary correto automaticamente)
      await api.post(`/cotacoes/${cotacaoId}/anexos`, formData);
      
      toast.success('Anexo enviado com sucesso!');
      fetchAnexos(cotacaoId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar anexo');
      throw error;
    } finally {
      setUploadingAnexo(false);
    }
  };

  const handleDeleteAnexo = async (cotacaoId, anexoId) => {
    try {
      await api.delete(`/cotacoes/${cotacaoId}/anexos/${anexoId}`);
      toast.success('Anexo removido!');
      fetchAnexos(cotacaoId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao remover anexo');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Drag & Drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e, cotacaoId) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      for (const file of files) {
        await handleUploadAnexo(cotacaoId, file);
      }
    }
  };

  const handleEnviarCotacao = async (cotacao) => {
    // Abrir modal de envio ao invés de enviar direto
    setShowEnviarDialog(cotacao);
  };

  const confirmarEnvio = async (metodo) => {
    if (!showEnviarDialog) return;
    
    try {
      // Primeiro, atualizar status para aguardando_respostas
      await api.put(`/cotacoes/${showEnviarDialog.id}/status`, { status: 'aguardando_respostas' });
      
      if (metodo === 'whatsapp') {
        // Abrir modal de WhatsApp para cada fornecedor
        setShowWhatsAppDialog(showEnviarDialog);
      } else if (metodo === 'email') {
        toast.info('Funcionalidade de email em desenvolvimento');
      } else if (metodo === 'pdf') {
        // Baixar PDF para envio manual
        await handleDownloadPdfCotacao(showEnviarDialog.id, showEnviarDialog.numero);
      }
      
      toast.success('Status da cotação atualizado!');
      setShowEnviarDialog(null);
      fetchCotacoes();
    } catch (error) {
      toast.error('Erro ao enviar cotação');
    }
  };

  const handleGerarPedido = async (cotacao) => {
    try {
      const response = await api.post(`/cotacoes/${cotacao.id}/gerar-pedido`, {});
      toast.success(`Pedido ${response.data.numero} gerado com sucesso!`);
      fetchCotacoes();
      fetchPedidos();
      // Limpar busca e mudar para aba de pedidos
      setSearchTerm('');
      setActiveTab('pedidos');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao gerar pedido');
    }
  };

  // ========== ASSISTENTE DE COMPRAS ==========

  // Contador de pendências para o badge da aba (carrega sem precisar abrir a aba)
  useEffect(() => {
    api.get('/compras/assistente/pendencias')
      .then((r) => setAssistenteTotal(r.data?.total || 0))
      .catch(() => {});
  }, []);

  const handleAssistenteAcao = async (p) => {
    try {
      if (p.recurso === 'cotacao') {
        const r = await api.get(`/cotacoes/${p.recurso_id}`);
        const cot = r.data;
        if (p.acao === 'gerar_pedido') {
          setShowMesaDecisao(cot);
        } else {
          setShowRespostaDialog(cot);
        }
      } else {
        const r = await api.get(`/pedidos-compra/${p.recurso_id}`);
        const ped = r.data;
        if (p.acao === 'registrar_recebimento') setShowReceberDialog(ped);
        else if (p.acao === 'conferir_custos') setShowConferenciaDialog(ped);
        else if (p.acao === 'abrir_financeiro') setShowParcelasDialog(ped);
      }
    } catch (error) {
      toast.error('Erro ao abrir o item da pendência');
    }
  };

  // ========== PEDIDOS ==========

  const handleAprovarPedido = async (pedido) => {
    try {
      await api.post(`/pedidos-compra/${pedido.id}/aprovar`, {});
      toast.success('Pedido aprovado!');
      fetchPedidos();
      // Pedido vinculado a projeto: abrir conferência de custos
      if (pedido.projeto_id) {
        setShowConferenciaDialog(pedido);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao aprovar pedido');
    }
  };

  const handleEnviarPedido = async (pedido) => {
    // Abrir modal de envio de pedido (similar ao de cotação)
    setShowEnviarPedidoDialog(pedido);
  };
  
  const confirmarEnvioPedido = async (opcao) => {
    if (!showEnviarPedidoDialog) return;
    const pedido = showEnviarPedidoDialog;
    
    try {
      if (opcao === 'pdf') {
        await handleDownloadPdfPedido(pedido.id, pedido.numero);
      } else if (opcao === 'whatsapp' && pedido.fornecedor_telefone) {
        // Gerar link WhatsApp para pedido
        const msg = encodeURIComponent(
          `Olá! Segue o Pedido de Compra Nº ${pedido.numero} referente à nossa solicitação. ` +
          `Valor total: R$ ${pedido.total?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}. ` +
          `Por favor, confirme o recebimento.`
        );
        const tel = pedido.fornecedor_telefone.replace(/\D/g, '');
        window.open(`https://wa.me/55${tel}?text=${msg}`, '_blank');
      }
      
      // Marcar como enviado
      await api.post(`/pedidos-compra/${pedido.id}/enviar`, {});
      toast.success('Pedido marcado como enviado ao fornecedor!');
      fetchPedidos();
      setShowEnviarPedidoDialog(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar pedido');
    }
  };

  // Abre o modal de recebimento com seleção de local
  const handleReceberPedido = (pedido) => {
    setShowReceberDialog(pedido);
  };
  
  // Callback quando o recebimento é concluído com sucesso
  const handleReceberPedidoSuccess = async () => {
    const pedido = showReceberDialog;
    
    // Se o pedido está vinculado a um projeto e os custos ainda não foram conferidos, abrir conferência
    if (pedido?.projeto_id && !pedido.custos_conferidos) {
      setShowConferenciaDialog(pedido);
    }
    
    fetchPedidos();
  };

  // ========== PARCELAS ==========
  
  const gerarParcelasAutomaticas = (pedido, numParcelas) => {
    if (!pedido || numParcelas < 1) return [];
    const valorTotal = pedido.total || 0;
    const valorParcela = Math.floor((valorTotal / numParcelas) * 100) / 100;
    const resto = valorTotal - (valorParcela * numParcelas);
    
    const parcelas = [];
    const hoje = new Date();
    
    for (let i = 0; i < numParcelas; i++) {
      const dataVencimento = new Date(hoje);
      dataVencimento.setDate(dataVencimento.getDate() + ((i + 1) * 30)); // 30, 60, 90 dias...
      
      parcelas.push({
        numero: i + 1,
        valor: i === 0 ? valorParcela + Math.round(resto * 100) / 100 : valorParcela,
        data_vencimento: dataVencimento.toISOString().split('T')[0],
        status: 'pendente'
      });
    }
    return parcelas;
  };
  
  const handleSalvarParcelas = async (pedidoId, parcelas, observacao = '', opts = {}) => {
    try {
      const { sincronizar_conta_azul = true } = opts;
      await axios.put(
        `${API}/pedidos-compra/${pedidoId}/parcelas`,
        { parcelas, observacao, sincronizar_conta_azul },
        getAuthHeader()
      );
      toast.success(sincronizar_conta_azul
        ? 'Parcelas salvas com sucesso!'
        : 'Parcelas salvas (sem envio ao Conta Azul).');
      fetchPedidos();
      setShowParcelasDialog(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar parcelas');
    }
  };

  // ========== CONFERÊNCIA DE CUSTOS DO PROJETO ==========
  
  const handleAtualizarCustosProjeto = (pedido) => {
    if (!pedido.projeto_id) {
      toast.info('Este pedido não está vinculado a um projeto');
      return;
    }
    setShowConferenciaDialog(pedido);
  };

  // ========== FORNECEDORES ==========

  const toggleFornecedor = (forn) => {
    setCotacaoForm(prev => {
      const isSelected = prev.fornecedores.some(f => f.fornecedor_id === forn.id);
      if (isSelected) {
        return {
          ...prev,
          fornecedores: prev.fornecedores.filter(f => f.fornecedor_id !== forn.id)
        };
      } else {
        const contato = forn.contatos?.[0] || {};
        // Sugestão (Fase A→C): herdar defaults do fornecedor estrangeiro.
        // Só sugere a moeda se o usuário ainda não mexeu (continua BRL) e o fornecedor tem moeda padrão estrangeira.
        const isPrimeiroForn = prev.fornecedores.length === 0;
        let novaMoeda = prev.moeda;
        let novaTaxa = prev.taxa_cambio;
        let novoIncoterm = prev.incoterm;
        let novoLead = prev.lead_time_dias;
        if (isPrimeiroForn) {
          const heranca = [];
          if ((!prev.moeda || prev.moeda === 'BRL') && forn.moeda_padrao && forn.moeda_padrao !== 'BRL') {
            novaMoeda = forn.moeda_padrao; heranca.push(`moeda ${forn.moeda_padrao}`);
          }
          if (!prev.incoterm && forn.incoterm_padrao) {
            novoIncoterm = forn.incoterm_padrao; heranca.push(`incoterm ${forn.incoterm_padrao}`);
          }
          if ((prev.lead_time_dias === '' || prev.lead_time_dias == null) && forn.lead_time_padrao_dias) {
            novoLead = forn.lead_time_padrao_dias; heranca.push(`lead time ${forn.lead_time_padrao_dias}d`);
          }
          if (heranca.length) {
            toast.info(`Herdado de ${getFornecedorNome(forn)}: ${heranca.join(', ')}`);
          }
        }
        return {
          ...prev,
          moeda: novaMoeda,
          taxa_cambio: novaTaxa,
          incoterm: novoIncoterm,
          lead_time_dias: novoLead,
          fornecedores: [
            ...prev.fornecedores,
            {
              fornecedor_id: forn.id,
              fornecedor_nome: getFornecedorNome(forn),
              contato_nome: contato.nome || forn.contato_nome || '',
              contato_email: contato.email || forn.email || '',
              contato_telefone: contato.telefone || forn.telefone || '',
            }
          ]
        };
      }
    });
  };

  // ========== ITENS ==========

  const addItemWithType = (tipo) => {
    const baseItem = {
      id: `item-${Date.now()}`,
      tipo: tipo,
      descricao: '',
      observacao: '',
      quantidade: 1,
      unidade: tipo === 'servico' ? 'SV' : tipo === 'frete' ? 'VB' : 'UN',
      marca: '',
      modelo: '',
      detalhes: '',
    };

    // Pre-preencher descrição para tipos específicos
    if (tipo === 'frete') {
      baseItem.descricao = 'Frete';
    } else if (tipo === 'reembolso') {
      baseItem.descricao = 'Reembolso';
      baseItem.unidade = 'VB';
    }

    setCotacaoForm(prev => ({
      ...prev,
      itens: [...prev.itens, baseItem]
    }));
  };

  const addItem = () => addItemWithType('outro');

  const updateItem = (index, field, value) => {
    setCotacaoForm(prev => ({
      ...prev,
      itens: prev.itens.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const removeItem = (index) => {
    setCotacaoForm(prev => ({
      ...prev,
      itens: prev.itens.filter((_, i) => i !== index)
    }));
  };

  // ========== FORNECEDORES NA COTAÇÃO ==========

  const addFornecedorCotacao = (fornecedor) => {
    if (cotacaoForm.fornecedores.find(f => f.fornecedor_id === fornecedor.id)) {
      toast.warning('Fornecedor já adicionado');
      return;
    }

    const contato = fornecedor.contatos?.[0] || {};
    
    setCotacaoForm(prev => ({
      ...prev,
      fornecedores: [
        ...prev.fornecedores,
        {
          fornecedor_id: fornecedor.id,
          fornecedor_nome: getFornecedorNome(fornecedor),
          contato_nome: contato.nome || fornecedor.contato_nome || '',
          contato_email: contato.email || fornecedor.email || '',
          contato_telefone: contato.telefone || fornecedor.telefone || '',
        }
      ]
    }));
  };

  const removeFornecedorCotacao = (index) => {
    setCotacaoForm(prev => ({
      ...prev,
      fornecedores: prev.fornecedores.filter((_, i) => i !== index)
    }));
  };

  // ========== FILTROS ==========

  // Opções derivadas (calculadas a partir de cotacoes/pedidos para popular os dropdowns)
  const statusOptionsCotacoes = useMemo(() => ([
    { id: 'rascunho', label: 'Rascunho' },
    { id: 'aguardando_respostas', label: 'Aguardando resposta' },
    { id: 'analisando', label: 'Analisando' },
    { id: 'aprovada_parcial', label: 'Decisão em curso' },
    { id: 'aprovada', label: 'Aprovada' },
    { id: 'cancelada', label: 'Cancelada' },
    { id: 'expirada', label: 'Expirada' },
  ]), []);
  const statusOptionsPedidos = useMemo(() => ([
    { id: 'aprovado', label: 'Aprovado' },
    { id: 'enviado', label: 'Enviado' },
    { id: 'recebido_parcial', label: 'Recebido Parcial' },
    { id: 'recebido', label: 'Recebido' },
    { id: 'cancelado', label: 'Cancelado' },
  ]), []);

  // Fornecedores que aparecem nas cotações
  const fornecedorOptionsCotacoes = useMemo(() => {
    const map = new Map();
    cotacoes.forEach(c => (c.fornecedores || []).forEach(f => {
      if (f.fornecedor_id && !map.has(f.fornecedor_id)) {
        map.set(f.fornecedor_id, f.fornecedor_nome || 'Sem nome');
      }
    }));
    return Array.from(map.entries()).map(([id, label]) => ({ id, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [cotacoes]);
  // Fornecedores nos pedidos
  const fornecedorOptionsPedidos = useMemo(() => {
    const map = new Map();
    pedidos.forEach(p => {
      if (p.fornecedor_id && !map.has(p.fornecedor_id)) {
        map.set(p.fornecedor_id, p.fornecedor_nome || 'Sem nome');
      }
    });
    return Array.from(map.entries()).map(([id, label]) => ({ id, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [pedidos]);
  // Projetos
  const projetoOptionsCotacoes = useMemo(() => {
    const map = new Map();
    cotacoes.forEach(c => { if (c.projeto_id) map.set(c.projeto_id, c.projeto_nome || c.projeto_id); });
    return Array.from(map.entries()).map(([id, label]) => ({ id, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [cotacoes]);
  const projetoOptionsPedidos = useMemo(() => {
    const map = new Map();
    pedidos.forEach(p => { if (p.projeto_id) map.set(p.projeto_id, p.projeto_nome || p.projeto_id); });
    return Array.from(map.entries()).map(([id, label]) => ({ id, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [pedidos]);
  // Itens (descrição é a chave - permite cruzar itens iguais entre cotações)
  const itemOptionsCotacoes = useMemo(() => {
    const set = new Set();
    cotacoes.forEach(c => (c.itens || []).forEach(it => {
      const desc = (it.descricao || '').trim();
      if (desc) set.add(desc);
    }));
    return Array.from(set).map(desc => ({ id: desc, label: desc })).sort((a, b) => a.label.localeCompare(b.label));
  }, [cotacoes]);
  const itemOptionsPedidos = useMemo(() => {
    const set = new Set();
    pedidos.forEach(p => (p.itens || []).forEach(it => {
      const desc = (it.descricao || '').trim();
      if (desc) set.add(desc);
    }));
    return Array.from(set).map(desc => ({ id: desc, label: desc })).sort((a, b) => a.label.localeCompare(b.label));
  }, [pedidos]);

  // Moedas presentes nos dados (para filtro "não contaminar")
  const moedaOptionsCotacoes = useMemo(() => {
    const set = new Set();
    cotacoes.forEach(c => set.add(c.moeda || 'BRL'));
    return Array.from(set).map(code => ({ id: code, label: `${getCurrencyMeta(code).symbol} ${code}` }));
  }, [cotacoes]);
  const moedaOptionsPedidos = useMemo(() => {
    const set = new Set();
    pedidos.forEach(p => set.add(p.moeda || 'BRL'));
    return Array.from(set).map(code => ({ id: code, label: `${getCurrencyMeta(code).symbol} ${code}` }));
  }, [pedidos]);

  // Datas — calcula a partir do preset + custom
  const rangeCotacoes = useMemo(() => computeDateRange(cotPeriodo, dataInicio, dataFim), [cotPeriodo, dataInicio, dataFim]);
  const rangePedidos = useMemo(() => computeDateRange(pedPeriodo, dataInicio, dataFim), [pedPeriodo, dataInicio, dataFim]);
  const rangeDashboard = useMemo(() => computeDateRange(dashPeriodo, dataInicio, dataFim), [dashPeriodo, dataInicio, dataFim]);

  // Stats do dashboard respeitando o filtro de período
  useEffect(() => {
    fetchDashboardStats(rangeDashboard).then(setDashData).catch(err => console.error('Erro ao carregar dashboard:', err));
  }, [rangeDashboard, statsVersion]);

  useEffect(() => {
    fetchEvolucaoMensal(rangeDashboard, dashMoeda).then(setEvolucaoData).catch(err => console.error('Erro ao carregar evolução mensal:', err));
  }, [rangeDashboard, statsVersion, dashMoeda]);

  const dentroDoRange = (dateStr, range) => {
    if (!range.inicio && !range.fim) return true;
    if (!dateStr) return false;
    const d = new Date(dateStr);
    if (range.inicio && d < range.inicio) return false;
    if (range.fim && d > range.fim) return false;
    return true;
  };

  // Itens urgentes (respeitam o filtro de período do dashboard)
  const itensUrgentes = useMemo(() => {
    const cotsFiltradas = cotacoes.filter(c => dentroDoRange(c.created_at, rangeDashboard));
    const pedsFiltrados = pedidos.filter(p => dentroDoRange(p.created_at, rangeDashboard));
    return [
      ...cotsFiltradas.filter(c => c.urgente && !['aprovada', 'cancelada'].includes(c.status)).map(c => ({
        tipo: 'cotacao', numero: c.numero, titulo: c.titulo, status: c.status
      })),
      ...pedsFiltrados.filter(p => p.urgente && !['recebido', 'cancelado'].includes(p.status)).map(p => ({
        tipo: 'pedido', numero: p.numero, titulo: p.fornecedor_nome, status: p.status
      }))
    ];
  }, [cotacoes, pedidos, rangeDashboard]);

  const COT_VIEW_ANDAMENTO = ['rascunho', 'aguardando_respostas', 'analisando', 'aprovada_parcial'];
  const COT_VIEW_CONCLUIDAS = ['aprovada', 'cancelada', 'excluida'];

  const filteredCotacoes = cotacoes.filter(cotacao => {
    const q = (searchTerm || '').toLowerCase();
    const matchSearch = !q ||
      cotacao.numero?.toLowerCase().includes(q) ||
      cotacao.titulo?.toLowerCase().includes(q) ||
      cotacao.fornecedores?.some(f => f.fornecedor_nome?.toLowerCase().includes(q));

    // Filtro por modo de visualização (só aplica quando não há filtro de status explícito e não há busca)
    let matchView = true;
    if (cotStatusSel.length === 0 && !q) {
      if (cotViewMode === 'em_andamento') matchView = COT_VIEW_ANDAMENTO.includes(cotacao.status);
      else if (cotViewMode === 'concluidas') matchView = COT_VIEW_CONCLUIDAS.includes(cotacao.status);
    }

    const matchStatus = cotStatusSel.length === 0 || cotStatusSel.includes(cotacao.status);
    const matchFornecedor = cotFornecedorSel.length === 0 ||
      (cotacao.fornecedores || []).some(f => cotFornecedorSel.includes(f.fornecedor_id));
    const matchProjeto = cotProjetoSel.length === 0 ||
      (cotacao.projeto_id && cotProjetoSel.includes(cotacao.projeto_id));
    const matchItem = cotItemSel.length === 0 ||
      (cotacao.itens || []).some(it => cotItemSel.includes((it.descricao || '').trim()));
    const matchMoeda = cotMoedaSel.length === 0 || cotMoedaSel.includes(cotacao.moeda || 'BRL');
    const matchUrgente = !cotUrgenteSel || !!cotacao.urgente;
    const matchData = dentroDoRange(cotacao.created_at, rangeCotacoes);

    return matchSearch && matchView && matchStatus && matchFornecedor && matchProjeto && matchItem && matchMoeda && matchUrgente && matchData;
  });

  // Contadores por modo (sobre a base completa, respeitando apenas busca textual)
  const cotViewCounts = (() => {
    const q = (searchTerm || '').toLowerCase();
    const base = cotacoes.filter(c => !q ||
      c.numero?.toLowerCase().includes(q) ||
      c.titulo?.toLowerCase().includes(q) ||
      c.fornecedores?.some(f => f.fornecedor_nome?.toLowerCase().includes(q)));
    return {
      em_andamento: base.filter(c => COT_VIEW_ANDAMENTO.includes(c.status)).length,
      concluidas: base.filter(c => COT_VIEW_CONCLUIDAS.includes(c.status)).length,
      todas: base.length,
    };
  })();

  const filteredPedidos = pedidos.filter(pedido => {
    const q = (searchTerm || '').toLowerCase();
    const matchSearch = !q ||
      pedido.numero?.toLowerCase().includes(q) ||
      pedido.fornecedor_nome?.toLowerCase().includes(q);

    const matchStatus = pedStatusSel.length === 0 || pedStatusSel.includes(pedido.status);
    const matchFornecedor = pedFornecedorSel.length === 0 ||
      (pedido.fornecedor_id && pedFornecedorSel.includes(pedido.fornecedor_id));
    const matchProjeto = pedProjetoSel.length === 0 ||
      (pedido.projeto_id && pedProjetoSel.includes(pedido.projeto_id));
    const matchItem = pedItemSel.length === 0 ||
      (pedido.itens || []).some(it => pedItemSel.includes((it.descricao || '').trim()));
    const matchMoeda = pedMoedaSel.length === 0 || pedMoedaSel.includes(pedido.moeda || 'BRL');
    const matchUrgente = !pedUrgenteSel || !!pedido.urgente;
    const matchData = dentroDoRange(pedido.created_at, rangePedidos);

    return matchSearch && matchStatus && matchFornecedor && matchProjeto && matchItem && matchMoeda && matchUrgente && matchData;
  });

  // ========== RENDER ==========

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="compras-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <TitleWithHelp
            title="Módulo de Compras"
            description="Gerencie todo o processo de compras: cotações, envio para fornecedores, análise comparativa, pedidos de compra com controle de parcelas e recebimento."
            connections={['Empresas', 'Fornecedores', 'Produtos', 'Projetos', 'Conta Azul']}
          >
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <ShoppingCart className="h-6 w-6 text-blue-600" />
              Compras
            </h1>
          </TitleWithHelp>
          <p className="text-gray-500 mt-1">Gerencie cotações e pedidos de compra</p>
        </div>
        <Button onClick={handleNovaCotacao} data-testid="btn-nova-cotacao">
          <Plus className="w-4 h-4 mr-2" />
          Nova Cotação
        </Button>
      </div>


      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">
            <TrendingUp className="w-4 h-4 mr-2" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="assistente" data-testid="tab-assistente">
            <Bot className="w-4 h-4 mr-2" />
            Assistente
            {assistenteTotal > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px] font-semibold leading-none">
                {assistenteTotal}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="cotacoes" data-testid="tab-cotacoes">
            <FileText className="w-4 h-4 mr-2" />
            Cotações ({cotacoes.length})
          </TabsTrigger>
          <TabsTrigger value="pedidos" data-testid="tab-pedidos">
            <ShoppingCart className="w-4 h-4 mr-2" />
            Pedidos ({pedidos.length})
          </TabsTrigger>
        </TabsList>

        {/* Filtros — faixa horizontal organizada abaixo das abas (oculta na aba Assistente) */}
        {activeTab !== 'assistente' && (
        <Card className="mt-3 mb-4 border-slate-200">
          <CardContent className="py-3">
            {activeTab === 'dashboard' && (
              <ComprasFilters
                showAdvanced={false}
                periodoPreset={dashPeriodo} setPeriodoPreset={setDashPeriodo}
                dataInicio={dataInicio} setDataInicio={setDataInicio}
                dataFim={dataFim} setDataFim={setDataFim}
              />
            )}
            {activeTab === 'cotacoes' && (
              <ComprasFilters
                searchTerm={searchTerm} setSearchTerm={setSearchTerm}
                searchPlaceholder="Buscar cotação (nº, título)..."
                statusSel={cotStatusSel} setStatusSel={setCotStatusSel}
                fornecedorSel={cotFornecedorSel} setFornecedorSel={setCotFornecedorSel}
                projetoSel={cotProjetoSel} setProjetoSel={setCotProjetoSel}
                itemSel={cotItemSel} setItemSel={setCotItemSel}
                moedaSel={cotMoedaSel} setMoedaSel={setCotMoedaSel}
                urgenteSel={cotUrgenteSel} setUrgenteSel={setCotUrgenteSel}
                statusOptions={statusOptionsCotacoes}
                fornecedorOptions={fornecedorOptionsCotacoes}
                projetoOptions={projetoOptionsCotacoes}
                itemOptions={itemOptionsCotacoes}
                moedaOptions={moedaOptionsCotacoes}
                periodoPreset={cotPeriodo} setPeriodoPreset={setCotPeriodo}
                dataInicio={dataInicio} setDataInicio={setDataInicio}
                dataFim={dataFim} setDataFim={setDataFim}
              />
            )}
            {activeTab === 'pedidos' && (
              <ComprasFilters
                searchTerm={searchTerm} setSearchTerm={setSearchTerm}
                searchPlaceholder="Buscar pedido (nº, fornecedor)..."
                statusSel={pedStatusSel} setStatusSel={setPedStatusSel}
                fornecedorSel={pedFornecedorSel} setFornecedorSel={setPedFornecedorSel}
                projetoSel={pedProjetoSel} setProjetoSel={setPedProjetoSel}
                itemSel={pedItemSel} setItemSel={setPedItemSel}
                moedaSel={pedMoedaSel} setMoedaSel={setPedMoedaSel}
                urgenteSel={pedUrgenteSel} setUrgenteSel={setPedUrgenteSel}
                statusOptions={statusOptionsPedidos}
                fornecedorOptions={fornecedorOptionsPedidos}
                projetoOptions={projetoOptionsPedidos}
                itemOptions={itemOptionsPedidos}
                moedaOptions={moedaOptionsPedidos}
                periodoPreset={pedPeriodo} setPeriodoPreset={setPedPeriodo}
                dataInicio={dataInicio} setDataInicio={setDataInicio}
                dataFim={dataFim} setDataFim={setDataFim}
              />
            )}
          </CardContent>
        </Card>
        )}

        {/* Assistente Tab */}
        <TabsContent value="assistente" className="mt-4">
          <AssistenteCompras onAcao={handleAssistenteAcao} onTotalChange={setAssistenteTotal} />
        </TabsContent>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="mt-4">
          <DashboardCompras
            data={dashData}
            evolucao={evolucaoData}
            itensUrgentes={itensUrgentes}
            moeda={dashMoeda}
            onMoedaChange={setDashMoeda}
            onNavigate={(tab, statuses) => {
              setSearchTerm('');
              if (tab === 'cotacoes') setCotStatusSel(statuses || []);
              if (tab === 'pedidos') setPedStatusSel(statuses || []);
              setActiveTab(tab);
            }}
            onVerItem={(item) => {
              setSearchTerm(item.numero);
              setActiveTab(item.tipo === 'cotacao' ? 'cotacoes' : 'pedidos');
            }}
          />
        </TabsContent>

        {/* Cotações Tab */}
        <TabsContent value="cotacoes" className="mt-4">
          {/* Atalhos de visualização — evita poluição de cotações já concluídas */}
          <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="cotacoes-view-chips">
            {[
              { key: 'em_andamento', label: 'Em andamento', hint: 'Rascunho, aguardando resposta, analisando e decisão em curso' },
              { key: 'concluidas', label: 'Concluídas', hint: 'Aprovadas, canceladas e excluídas' },
              { key: 'todas', label: 'Todas', hint: 'Todas as cotações' },
            ].map(v => (
              <button
                key={v.key}
                type="button"
                title={v.hint}
                onClick={() => setCotViewMode(v.key)}
                data-testid={`cot-view-${v.key}`}
                className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                  cotViewMode === v.key
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {v.label}
                <span className={`ml-1.5 text-xs ${cotViewMode === v.key ? 'text-blue-100' : 'text-gray-400'}`}>
                  {cotViewCounts[v.key]}
                </span>
              </button>
            ))}
            {(searchTerm || cotStatusSel.length > 0) && (
              <span className="text-xs text-gray-400 ml-1">
                (filtro/busca ativo — exibindo todos os status correspondentes)
              </span>
            )}
          </div>
          <CotacaoList
            cotacoes={filteredCotacoes}
            isAdmin={isAdmin}
            onNovaCotacao={handleNovaCotacao}
            onViewCotacao={(c) => setShowCotacaoView(c)}
            onEditCotacao={handleEditCotacao}
            onEnviarCotacao={handleEnviarCotacao}
            onOpenRespostaDialog={handleOpenRespostaDialog}
            onOpenMesaDecisao={handleOpenMesaDecisao}
            onGerarPedido={handleGerarPedido}
            onDeleteCotacao={(id) => { setDeleteCotacaoId(id); setDeleteMotivo(''); }}
            onPreviewPdf={handlePreviewPdf}
            onDownloadPdf={handleDownloadPdfCotacao}
          />
        </TabsContent>

        {/* Pedidos Tab */}
        <TabsContent value="pedidos" className="mt-4">
          <PedidoList
            pedidos={filteredPedidos}
            isAdmin={isAdmin}
            onViewPedido={handleOpenPedidoDetalhes}
            onAprovarPedido={handleAprovarPedido}
            onEnviarPedido={handleEnviarPedido}
            onReceberPedido={handleReceberPedido}
            onOpenParcelas={(p) => setShowParcelasDialog(p)}
            onOpenConferencia={(p) => setShowConferenciaDialog(p)}
            onPreviewPdf={handlePreviewPdf}
            onDownloadPdf={handleDownloadPdfPedido}
            onDeletePedido={(id) => { setDeletePedidoId(id); setDeleteMotivo(''); }}
          />
        </TabsContent>
      </Tabs>

      {/* Dialog Nova/Editar Cotação */}
      <CotacaoEditDialog
        open={showCotacaoDialog}
        onClose={() => { setShowCotacaoDialog(false); setCotacaoStep(1); }}
        editingCotacao={editingCotacao}
        cotacaoForm={cotacaoForm}
        setCotacaoForm={setCotacaoForm}
        cotacaoStep={cotacaoStep}
        empresas={empresas}
        projetos={projetos}
        produtos={produtos}
        tiposServico={tiposServico}
        tiposServicoFiltrados={tiposServicoFiltrados}
        fornecedores={fornecedores}
        aprovadores={aprovadores}
        aprovadorSearch={aprovadorSearch}
        setAprovadorSearch={setAprovadorSearch}
        fornSearchCot={fornSearchCot}
        setFornSearchCot={setFornSearchCot}
        anexosCotacaoForm={anexosCotacaoForm}
        anexosPendentes={anexosPendentes}
        setAnexosPendentes={setAnexosPendentes}
        submitLoading={submitLoading}
        onAvancar={handleAvancar}
        onVoltar={handleVoltar}
        onToggleAprovador={toggleAprovadorCotacao}
        onAddItem={addItemWithType}
        onUpdateItem={updateItem}
        onRemoveItem={removeItem}
        onToggleFornecedor={toggleFornecedor}
        onCadastrarProduto={() => setShowCadastroRapidoProduto(true)}
        onCadastrarFornecedor={() => setShowCadastroRapidoFornecedor(true)}
        onUploadAnexo={async (cotId, file, itemId) => { await handleUploadAnexo(cotId, file, itemId); await fetchAnexosForm(cotId); }}
        onDeleteAnexo={async (cotId, anexoId) => { await handleDeleteAnexo(cotId, anexoId); await fetchAnexosForm(cotId); }}
        onPatchAnexoForm={async (cotId, anexoId, patch) => { await api.patch(`/cotacoes/${cotId}/anexos/${anexoId}`, patch); await fetchAnexosForm(cotId); }}
        onSave={handleSaveCotacao}
      />

      {/* Dialog Visualizar Cotação */}
      <CotacaoViewDialog
        cotacao={showCotacaoView}
        onClose={() => setShowCotacaoView(null)}
        expandedFornecedor={expandedFornecedor}
        setExpandedFornecedor={setExpandedFornecedor}
        anexos={anexos}
        onUploadAnexo={handleUploadAnexo}
        onDeleteAnexo={handleDeleteAnexo}
        onPatchAnexo={async (cotacaoId, anexoId, patch) => {
          await api.patch(`/cotacoes/${cotacaoId}/anexos/${anexoId}`, patch);
          await fetchAnexos(cotacaoId);
        }}
      />

      {/* Alert Dialogs de confirmação (editar cotação c/ respostas, excluir cotação, excluir pedido) */}
      <ComprasConfirmDialogs
        confirmEditCotacao={confirmEditCotacao}
        onCloseEditConfirm={() => setConfirmEditCotacao(null)}
        onConfirmEdit={(cot) => { setConfirmEditCotacao(null); if (cot) abrirFormEdicaoCotacao(cot); }}
        deleteCotacaoId={deleteCotacaoId}
        onCloseDeleteCotacao={() => { setDeleteCotacaoId(null); setDeleteMotivo(''); }}
        onConfirmDeleteCotacao={handleDeleteCotacao}
        deletePedidoId={deletePedidoId}
        onCloseDeletePedido={() => { setDeletePedidoId(null); setDeleteMotivo(''); }}
        onConfirmDeletePedido={handleDeletePedido}
        deleteMotivo={deleteMotivo}
        onDeleteMotivoChange={setDeleteMotivo}
        confirmCloseResposta={confirmCloseResposta}
        onConfirmCloseRespostaChange={setConfirmCloseResposta}
        respostaPendingCount={respostaPendingCount}
        onDiscardResposta={() => {
          setShowRespostaDialog(null);
          setRespostaPendingCount(0);
          setConfirmCloseResposta(false);
        }}
      />

      {/* Dialog Registrar Resposta */}
      <Dialog
        open={!!showRespostaDialog}
        onOpenChange={(open) => {
          if (!open) {
            // Tentativa de fechar (clique fora, ESC, X)
            if (respostaPendingCount > 0) {
              setConfirmCloseResposta(true);  // pede confirmação
            } else {
              setShowRespostaDialog(null);
              setRespostaPendingCount(0);
            }
          }
        }}
      >
        <DialogContent
          className="max-w-4xl max-h-[90vh] overflow-y-auto"
          onInteractOutside={(e) => {
            // Bloqueia clique fora se houver alterações pendentes
            if (respostaPendingCount > 0) {
              e.preventDefault();
              setConfirmCloseResposta(true);
            }
          }}
          onEscapeKeyDown={(e) => {
            if (respostaPendingCount > 0) {
              e.preventDefault();
              setConfirmCloseResposta(true);
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>Registrar Resposta de Fornecedor</DialogTitle>
          </DialogHeader>

          {showRespostaDialog && (
            <RespostaFornecedorForm
              cotacao={showRespostaDialog}
              onPendingChange={setRespostaPendingCount}
              onCotacaoChange={async (updatedCotacao) => {
                if (updatedCotacao) {
                  setShowRespostaDialog(updatedCotacao);
                  return updatedCotacao;
                }
                // Sem arg → refetch para refletir mudanças (ex: adoção de alternativa)
                try {
                  const r = await api.get(`/cotacoes/${showRespostaDialog.id}`);
                  setShowRespostaDialog(r.data);
                  return r.data;
                } catch (e) {
                  // Em último caso, não fecha o dialog — falha silenciosa
                  return null;
                }
              }}
              onAllSaved={() => {
                setShowRespostaDialog(null);
                setRespostaPendingCount(0);
                fetchCotacoes();
              }}
              onSave={async (fornecedorId, resposta, opts = {}) => {
                try {
                  await api.post(`/cotacoes/${showRespostaDialog.id}/resposta/${fornecedorId}`, resposta);
                  if (!opts.silent) {
                    toast.success('Resposta registrada!');
                    setShowRespostaDialog(null);
                    setRespostaPendingCount(0);
                    fetchCotacoes();
                  }
                } catch (error) {
                  toast.error(error.response?.data?.detail || 'Erro ao salvar resposta');
                  throw error;
                }
              }}
              onCancel={() => {
                if (respostaPendingCount > 0) {
                  setConfirmCloseResposta(true);
                } else {
                  setShowRespostaDialog(null);
                }
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Mesa de Decisão (Fase 4) — também faz o papel de Comparativo (Score + Aprovar tudo) */}
      {showMesaDecisao && (
        <MesaDecisao
          cotacao={showMesaDecisao}
          onReload={async () => {
            // Re-fetch a cotação atualizada
            try {
              const r = await api.get(`/cotacoes/${showMesaDecisao.id}`);
              setShowMesaDecisao(r.data);
            } catch {}
            fetchCotacoes();
          }}
          onPedidosGerados={() => {
            toast.success('Pedidos gerados com sucesso! Verifique a aba Pedidos.');
            fetchCotacoes();
            fetchPedidos();
          }}
          onEditCotacao={(cot) => {
            setShowMesaDecisao(null);
            handleEditCotacao(cot);
          }}
          onRegistrarResposta={(cot) => {
            setShowMesaDecisao(null);
            handleOpenRespostaDialog(cot);
          }}
          onClose={() => setShowMesaDecisao(null)}
        />
      )}

      {/* Dialog WhatsApp */}
      <WhatsAppEnvioDialog
        cotacao={showWhatsAppDialog}
        onClose={() => setShowWhatsAppDialog(null)}
        onGerarLink={async (cotacaoId, fornecedorId, fornecedorNome) => {
          try {
            const response = await api.get(`/cotacoes/${cotacaoId}/whatsapp-link/${fornecedorId}`);
            window.open(response.data.link, '_blank');
            toast.success(`Link WhatsApp gerado para ${fornecedorNome}`);
            setShowWhatsAppDialog(null);
          } catch (error) {
            toast.error('Erro ao gerar link WhatsApp');
          }
        }}
      />

      {/* Dialog Enviar Cotação */}
      <EnviarCotacaoDialog
        cotacao={showEnviarDialog}
        onClose={() => setShowEnviarDialog(null)}
        onConfirmarEnvio={confirmarEnvio}
      />

      {/* Dialog Enviar Pedido de Compra */}
      <EnviarPedidoDialog
        pedido={showEnviarPedidoDialog}
        onClose={() => setShowEnviarPedidoDialog(null)}
        onConfirmarEnvio={confirmarEnvioPedido}
      />

      {/* Dialog Detalhes do Pedido */}
      <PedidoDetalhesDialog
        pedido={showPedidoDialog}
        onClose={() => setShowPedidoDialog(null)}
        onAtualizarCustos={handleAtualizarCustosProjeto}
        onPreviewPdf={handlePreviewPdf}
        onDownloadPdf={handleDownloadPdfPedido}
        onSyncPagamento={async (p) => {
          try {
            const resp = await api.post(`/pedidos-compra/${p.id}/sync-pagamento-conta-azul`);
            if (resp.data?.integrado === false) {
              toast.warning(resp.data?.message || 'Empresa não integrada ao Conta Azul');
              return;
            }
            const r = resp.data || {};
            toast.success(`Status atualizado: ${r.atualizadas || 0} parcela(s) alterada(s), ${r.consultadas || 0} consultada(s)`);
            await fetchPedidos();
            if (Array.isArray(r.parcelas)) {
              setShowPedidoDialog(prev => prev && prev.id === p.id ? { ...prev, parcelas: r.parcelas, pagamento_last_sync: new Date().toISOString() } : prev);
            }
          } catch (e) {
            toast.error('Erro ao atualizar status do Conta Azul');
          }
        }}
        onSaveRecado={async (p, recado) => {
          try {
            const resp = await api.put(`/pedidos-compra/${p.id}/recado-financeiro`, { recado });
            toast.success('Recado do Financeiro salvo');
            await fetchPedidos();
            setShowPedidoDialog(prev => prev && prev.id === p.id ? {
              ...prev,
              recado_financeiro: recado,
              recado_financeiro_by: resp.data?.recado_financeiro_by,
            } : prev);
          } catch (e) {
            toast.error('Erro ao salvar recado');
          }
        }}
        onSaveTaxa={async (p, taxa) => {
          try {
            await api.put(`/pedidos-compra/${p.id}`, { taxa_cambio: taxa });
            toast.success('Taxa de câmbio salva');
            await fetchPedidos();
            setShowPedidoDialog(prev => prev && prev.id === p.id ? { ...prev, taxa_cambio: taxa } : prev);
          } catch (e) {
            toast.error('Erro ao salvar taxa de câmbio');
          }
        }}
        onAplicarDesconto={async (p, { tipo, valor, motivo }) => {
          try {
            const resp = await api.post(`/pedidos-compra/${p.id}/desconto-adicional`, { tipo, valor, motivo });
            toast.success('Desconto adicional aplicado');
            await fetchPedidos();
            setShowPedidoDialog(prev => prev && prev.id === p.id ? { ...prev, ...resp.data } : prev);
          } catch (e) {
            toast.error(e?.response?.data?.detail || 'Erro ao aplicar desconto');
          }
        }}
        onRemoverDesconto={async (p) => {
          try {
            const resp = await api.delete(`/pedidos-compra/${p.id}/desconto-adicional`);
            toast.success('Desconto adicional removido');
            await fetchPedidos();
            setShowPedidoDialog(prev => prev && prev.id === p.id ? { ...prev, ...resp.data } : prev);
          } catch (e) {
            toast.error('Erro ao remover desconto');
          }
        }}
        onSalvarParcelas={async (pedidoId, parcelas, observacao, opts) => {
          await handleSalvarParcelas(pedidoId, parcelas, observacao, opts);
          await fetchPedidos();
          setShowPedidoDialog(prev => prev && prev.id === pedidoId ? { ...prev, parcelas } : prev);
        }}
        uploadAnexo={async (file) => {
          if (!showPedidoDialog) return null;
          const fd = new FormData();
          fd.append('file', file);
          const r = await api.post(`/pedidos-compra/${showPedidoDialog.id}/anexos`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          return r.data;
        }}
        deleteAnexo={async (anexoId) => {
          if (!showPedidoDialog) return;
          await api.delete(`/pedidos-compra/${showPedidoDialog.id}/anexos/${anexoId}`);
        }}
        onAnexosChange={(p, novosAnexos) => {
          setShowPedidoDialog(prev => prev && prev.id === p.id ? { ...prev, anexos: novosAnexos } : prev);
          setPedidos(prev => prev.map(x => x.id === p.id ? { ...x, anexos: novosAnexos } : x));
        }}
        onAddComentario={async (p, texto) => {
          try {
            const resp = await api.post(`/pedidos-compra/${p.id}/comentarios`, { texto });
            const novo = resp.data?.comentario;
            toast.success('Comentário adicionado');
            setShowPedidoDialog(prev => prev && prev.id === p.id
              ? { ...prev, comentarios: [...(prev.comentarios || []), novo] }
              : prev);
            setPedidos(prev => prev.map(x => x.id === p.id
              ? { ...x, comentarios: [...(x.comentarios || []), novo] }
              : x));
          } catch (e) {
            toast.error(e?.response?.data?.detail || 'Erro ao adicionar comentário');
          }
        }}
      />

      {/* Dialog Financeiro: Parcelas + Anexos */}
      <Dialog open={!!showParcelasDialog} onOpenChange={() => setShowParcelasDialog(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-blue-600" />
              Financeiro do Pedido
            </DialogTitle>
          </DialogHeader>

          {showParcelasDialog && (
            <Tabs defaultValue="parcelas" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="parcelas" data-testid="tab-financeiro-parcelas">
                  <Calendar className="w-4 h-4 mr-1" /> Parcelas de Pagamento
                </TabsTrigger>
                <TabsTrigger value="anexos" data-testid="tab-financeiro-anexos">
                  <Paperclip className="w-4 h-4 mr-1" /> Anexos
                </TabsTrigger>
              </TabsList>

              <TabsContent value="parcelas" className="mt-3">
                <ParcelasForm
                  pedido={showParcelasDialog}
                  onSave={(parcelas, observacao, opts) => handleSalvarParcelas(showParcelasDialog.id, parcelas, observacao, opts)}
                  onCancel={() => setShowParcelasDialog(null)}
                  gerarParcelas={gerarParcelasAutomaticas}
                />
              </TabsContent>

              <TabsContent value="anexos" className="mt-3">
                <AnexosManager
                  anexos={showParcelasDialog.anexos || []}
                  onChange={(novosAnexos) => {
                    setShowParcelasDialog(prev => ({ ...prev, anexos: novosAnexos }));
                    setPedidos(prev => prev.map(p => p.id === showParcelasDialog.id
                      ? { ...p, anexos: novosAnexos }
                      : p));
                  }}
                  showLabel={false}
                  uploadHandler={async (file) => {
                    const fd = new FormData();
                    fd.append('file', file);
                    const r = await api.post(`/pedidos-compra/${showParcelasDialog.id}/anexos`, fd, {
                      headers: { 'Content-Type': 'multipart/form-data' }
                    });
                    return r.data;
                  }}
                  deleteHandler={async (anexoId) => {
                    await api.delete(`/pedidos-compra/${showParcelasDialog.id}/anexos/${anexoId}`);
                  }}
                />
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Receber Pedido com Seleção de Local */}
      <DialogReceberPedido
        open={!!showReceberDialog}
        onClose={() => setShowReceberDialog(null)}
        pedido={showReceberDialog}
        onSuccess={handleReceberPedidoSuccess}
        getAuthHeader={getAuthHeader}
      />

      {/* Dialog Conferência de Custos do Projeto */}
      <ConferenciaCustosDialog
        pedido={showConferenciaDialog}
        open={!!showConferenciaDialog}
        onClose={() => setShowConferenciaDialog(null)}
        onSuccess={() => { setShowConferenciaDialog(null); fetchPedidos(); }}
      />

      {/* Dialog Cadastro Rápido de Produto */}
      <CadastroRapidoProdutoDialog
        open={showCadastroRapidoProduto}
        onClose={() => setShowCadastroRapidoProduto(false)}
        origem="Compras"
        testIdPrefix="quick-comp"
        onSuccess={() => { fetchProdutos(); }}
      />

      {/* Dialog Cadastro Rápido de Fornecedor */}
      <CadastroRapidoFornecedorDialog
        open={showCadastroRapidoFornecedor}
        onOpenChange={setShowCadastroRapidoFornecedor}
        form={novoFornecedorForm}
        setForm={setNovoFornecedorForm}
        onSave={handleSaveFornecedorRapido}
      />

      {/* Modal Preview PDF */}
      <PdfPreviewInlineDialog
        preview={showPdfPreview}
        onClose={() => setShowPdfPreview(null)}
        onDownload={(preview) => {
          if (preview.type === 'cotacao') {
            handleDownloadPdfCotacao(preview.id, preview.numero);
          } else {
            handleDownloadPdfPedido(preview.id, preview.numero);
          }
        }}
      />
    </div>
  );
};

export default Compras;
